<template>
  <div class="transfer-content">
    <div v-if="loading">
      <AppLoading></AppLoading>
    </div>
    <div v-else>
      <div class="search-container">
        <inline-search v-model="searchText" @search="fetchAndMapTransferData"></inline-search>
      </div>
      <NavigableTabs>
        <b-tab :id="TAB_NAMES.DRAFT">
          <template #title>
            <h4>Draft ({{ draftTransfers.length }})</h4>
          </template>
          <b-table class="transfer-list" :fields="DRAFT_FIELDS" :items="draftTransfers" table-class="table-minimal">
            <template v-slot:cell(type)="data">
              <Users
                height="20"
                width="20"
                :color="PaletteColors['default-lighten-20']"
                v-if="data.item.type === 'bulk'"
              >
              </Users>
              <User height="20" width="20" :color="PaletteColors['default-lighten-20']" v-else></User>
            </template>
            <template v-slot:cell(amount)="data">
              {{ toCurrency(data.item.amount, 'USD') }}
            </template>
            <template v-slot:cell(delete)="data">
              <div
                class="go-to"
                v-b-modal="`delete-batch-${data?.item?.off_cycle_payment_group_id}`"
                :id="`delete-batch-${data?.item?.off_cycle_payment_group_id}`"
              >
                <Trash :color="PaletteColors['danger']" width="20" height="20" />
              </div>
              <b-modal
                :id="`delete-batch-${data.item.off_cycle_payment_group_id}`"
                title="Do you want to delete this batch?"
                ok-title="Yes, delete"
                ok-variant="danger"
                size="lg"
                centered
                @ok="deleteBatch(data.item)"
              >
                Once you delete this batch, it will be permanently removed from the system.
              </b-modal>
            </template>
            <template v-slot:cell(goTo)="data">
              <div class="go-to" @click="viewBatch(data?.item)" :id="`see-more-${data?.item?.batch_number}`">
                <CaretRight />
              </div>
            </template>
          </b-table>
        </b-tab>
        <b-tab :id="TAB_NAMES.PROCESSING">
          <template #title>
            <h4>Processing ({{ processingTransfers.length }})</h4>
          </template>
          <b-table
            class="transfer-list"
            :fields="IN_PROGRESS_FIELDS"
            :items="processingTransfers"
            table-class="table-minimal"
          >
            <template v-slot:cell(type)="data">
              <Users
                height="20"
                width="20"
                :color="PaletteColors['default-lighten-20']"
                v-if="data.item.type === 'bulk'"
              >
              </Users>
              <User height="20" width="20" :color="PaletteColors['default-lighten-20']" v-else></User>
            </template>
            <template v-slot:cell(amount)="data">
              {{ toCurrency(data.item.amount, 'USD') }}
            </template>
            <template v-slot:cell(goTo)="data">
              <div class="go-to" @click="viewBatch(data?.item)" :id="`see-more-${data?.item?.batch_number}`">
                <CaretRight />
              </div>
            </template>
          </b-table>
        </b-tab>
        <b-tab :id="TAB_NAMES.COMPLETED">
          <template #title>
            <h4>Completed ({{ completedTransfers.length }})</h4>
          </template>
          <b-table
            class="transfer-list"
            :fields="COMPLETED_FIELDS"
            :items="completedTransfers"
            table-class="table-minimal"
          >
            <template v-slot:cell(type)="data">
              <Users
                height="20"
                width="20"
                :color="PaletteColors['default-lighten-20']"
                v-if="data.item.type === 'bulk'"
              >
              </Users>
              <User height="20" width="20" :color="PaletteColors['default-lighten-20']" v-else></User>
            </template>
            <template v-slot:cell(reason)="data">
              <PaymentTypePill :reason="data.item.reason" :reason-options="paymentReasons" />
            </template>
            <template v-slot:cell(amount)="data">
              {{ toCurrency(data.item.amount, 'USD') }}
            </template>
            <template #cell(download_invoice)="data">
              <div
                class="go-to"
                @click="downloadInvoice(data?.item?.invoice_id)"
                :id="`invoice-${data?.item?.invoice_id}`"
              >
                <b-spinner
                  v-if="isDownloading !== null && isDownloading === data?.item?.invoice_id"
                  class="download-spinner"
                  variant="primary"
                />
                <Download :color="PaletteColors['brand-primary']" v-else />
              </div>
            </template>
            <template v-slot:cell(goTo)="data">
              <div class="go-to" @click="viewBatch(data?.item)" :id="`see-more-${data?.item?.batch}`">
                <CaretRight />
              </div>
            </template>
          </b-table>
        </b-tab>
        <b-tab :id="TAB_NAMES.FAILED">
          <template #title>
            <h4>Failed ({{ failedTransfers.length }})</h4>
          </template>
          <b-table class="transfer-list" :fields="FAILED_FIELDS" :items="failedTransfers" table-class="table-minimal">
            <template v-slot:cell(type)="data">
              <Users
                height="20"
                width="20"
                :color="PaletteColors['default-lighten-20']"
                v-if="data.item.type === 'bulk'"
              >
              </Users>
              <User height="20" width="20" :color="PaletteColors['default-lighten-20']" v-else></User>
            </template>
            <template v-slot:cell(reason)="data">
              <PaymentTypePill :reason="data.item.reason" :reason-options="paymentReasons" />
            </template>
            <template v-slot:cell(amount)="data">
              {{ toCurrency(data.item.amount, 'USD') }}
            </template>
            <template v-slot:cell(hide)="data">
              <div
                class="go-to"
                :id="`hide-batch-${data.item.off_cycle_payment_group_id}`"
                v-b-modal="`hide-batch-${data.item.off_cycle_payment_group_id}`"
              >
                <Trash :color="PaletteColors['danger']" width="20" height="20" />
              </div>
              <b-modal
                :id="`hide-batch-${data.item.off_cycle_payment_group_id}`"
                size="lg"
                title="Do you want to delete this batch?"
                ok-title="Yes"
                ok-variant="danger"
                centered
                @ok="hideBatch(data.item)"
              >
                Please confirm that you want to delete this payment. You will not be able to view this failed payment
                again.
              </b-modal>
            </template>
            <template v-slot:cell(goTo)="data">
              <div class="go-to" @click="viewBatch(data?.item)" :id="`see-more-${data?.item?.batch}`">
                <CaretRight />
              </div>
            </template>
          </b-table>
        </b-tab>
      </NavigableTabs>
    </div>
  </div>
</template>

<script setup>
import Users from '@/assets/icons/Users.vue';
import User from '@/assets/icons/User.vue';
import { ref, watch, computed } from 'vue';
import { PaletteColors } from '@/Theme';
import { format } from 'date-fns';
import CaretRight from '@/assets/icons/CaretRight.vue';
import { useRouter } from 'vue-router/composables';
import { useQuery, useMutation } from '@tanstack/vue-query';
import {
  getOffCycleConfig,
  getOffCyclePayments,
  deleteOffCycleBatch,
  hideOffCycleBatch,
  downloadOffCycleInvoice,
} from '@/queries/one-time-payments';
import { toCurrency } from '@/utils';
import AppLoading from '@/components/AppLoading.vue';
import PaymentTypePill from '@/pages/one-time-payments/components/payment-type-pill.vue';
import NavigableTabs from '@/components/NavigableTabs.vue';
import Trash from '@/assets/icons/Trash.vue';
import InlineSearch from '@/components/InlineSearch.vue';
import Download from '@/assets/icons/Download.vue';

const TAB_NAMES = {
  DRAFT: 'draft',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

const draftTransfers = ref([]);
const processingTransfers = ref([]);
const completedTransfers = ref([]);
const failedTransfers = ref([]);
const searchText = ref('');
const isDownloading = ref(null);

const loading = ref(true);
const paymentReasons = ref([]);

const router = useRouter();

const getPaymentsQuery = useQuery(getOffCyclePayments());
const offCycleConfig = useQuery(getOffCycleConfig());
const deleteBatchMutation = useMutation(deleteOffCycleBatch());
const hideBatchMutation = useMutation(hideOffCycleBatch());
const downloadInvoiceMutation = useMutation(downloadOffCycleInvoice());

watch([getPaymentsQuery.fetchStatus, getPaymentsQuery.isPending], () => {
  loading.value = getPaymentsQuery.isFetching;
  if (getPaymentsQuery.data.value) {
    fetchAndMapTransferData();
  }
});

watch([offCycleConfig.fetchStatus, offCycleConfig.isPending], () => {
  if (offCycleConfig.data.value) {
    paymentReasons.value = offCycleConfig.data.value.supported_payment_types;
  }
});

// All fields for the tables in the tabs
const RAW_DRAFT_FIELDS = [
  {
    key: 'type',
    label: '',
    thStyle: { width: '30px' },
    tdStyle: { width: '30px' },
  },
  {
    key: 'batch',
    label: 'Batch',
    formatter: (value, col, data) => {
      return data?.employee_name
        ? `${data.batch} • ${data.employee_name}`
        : `${data.batch} • ${data.number_of_payees} payee${data.number_of_payees === 1 ? '' : 's'}`;
    },
  },
  {
    key: 'created_at',
    label: 'Created at',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'amount',
    label: 'Amount',
    tdClass: 'text-right',
    thStyle: { textAlign: 'right' },
  },
  {
    key: 'delete',
    label: '',
    thStyle: { width: '20px' },
  },
  {
    key: 'goTo',
    label: '',
    thStyle: { width: '20px' },
  },
];

const RAW_IN_PROGRESS_FIELDS = [
  {
    key: 'type',
    label: '',
    thStyle: { width: '30px' },
    tdStyle: { width: '30px' },
  },
  {
    key: 'batch',
    label: 'Batch',
    formatter: (value, col, data) => {
      return data?.item?.employee_name
        ? `${data.batch} • ${data.employee_name}`
        : `${data.batch} • ${data.number_of_payees} payee${data.number_of_payees === 1 ? '' : 's'}`;
    },
  },
  {
    key: 'created_at',
    label: 'Created at',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'amount',
    label: 'Amount',
    tdClass: 'text-right',
    thStyle: { textAlign: 'right' },
  },
  {
    key: 'goTo',
    label: '',
    thStyle: { width: '20px' },
  },
];

const RAW_COMPLETED_FIELDS = [
  {
    key: 'type',
    label: '',
    thStyle: { width: '30px' },
    tdStyle: { width: '30px' },
  },
  {
    key: 'employee_name',
    label: 'Employee/Batch',
    formatter: (value, col, data) => {
      return data.employee_name
        ? `${data.batch} • ${data.employee_name}`
        : `${data.batch} • ${data.number_of_payees} payee${data.number_of_payees === 1 ? '' : 's'}`;
    },
  },
  {
    key: 'submitted_at',
    label: 'Submitted at',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'reason',
    label: 'Reason',
    tdClass: 'd-flex justify-content-end',
    thStyle: { textAlign: 'right' },
  },
  {
    key: 'billing_type',
    label: 'Paid with',
    tdClass: 'text-right text-capitalize',
    thStyle: { textAlign: 'right' },
    formatter: value => value.split('_').join(' ').toLowerCase(),
  },
  {
    key: 'amount',
    label: 'Amount',
    tdClass: 'text-right',
    thStyle: { textAlign: 'right' },
  },
  {
    key: 'download_invoice',
    label: '',
    thStyle: { width: '20px' },
    tdStyle: { width: '20px' },
  },
  {
    key: 'goTo',
    label: '',
    thStyle: { width: '20px' },
    tdStyle: { width: '20px' },
  },
];

const RAW_FAILED_FIELDS = [
  {
    key: 'type',
    label: '',
    thStyle: { width: '30px' },
    tdStyle: { width: '30px' },
  },
  {
    key: 'employee_name',
    label: 'Employee/Batch',
    formatter: (value, col, data) => {
      return data.employee_name
        ? `${data.batch} • ${data.employee_name}`
        : `${data.batch} • ${data.number_of_payees} payee${data.number_of_payees === 1 ? '' : 's'}`;
    },
  },
  {
    key: 'submitted_at',
    label: 'Submitted at',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'reason',
    label: 'Reason',
    tdClass: 'd-flex justify-content-end',
    thStyle: { textAlign: 'right' },
  },
  {
    key: 'billing_type',
    label: 'Paid with',
    tdClass: 'text-right text-capitalize',
    thStyle: { textAlign: 'right' },
    formatter: value => value.split('_').join(' ').toLowerCase(),
  },
  {
    key: 'amount',
    label: 'Amount',
    tdClass: 'text-right',
    thStyle: { textAlign: 'right' },
  },
  {
    key: 'hide',
    label: '',
    thStyle: { width: '20px' },
    tdStyle: { width: '20px' },
  },
  {
    key: 'goTo',
    label: '',
    thStyle: { width: '20px' },
    tdStyle: { width: '20px' },
  },
];

// Filtered fields for the tables in the tabs
const DRAFT_FIELDS = computed(() => {
  return RAW_DRAFT_FIELDS.filter(field => !field.hide);
});

const IN_PROGRESS_FIELDS = computed(() => {
  return RAW_IN_PROGRESS_FIELDS.filter(field => !field.hide);
});

const COMPLETED_FIELDS = computed(() => {
  return RAW_COMPLETED_FIELDS.filter(field => !field.hide);
});

const FAILED_FIELDS = computed(() => {
  return RAW_FAILED_FIELDS.filter(field => !field.hide);
});

// Methods
const fetchAndMapTransferData = async () => {
  const data = getPaymentsQuery.data.value || [];

  const mappedTransferData = data
    .filter(batch => !batch.properties?.hidden)
    .filter(batch => {
      if (searchText.value) {
        return batch.full_name.toLowerCase().includes(searchText.value.toLowerCase());
      }
      return true;
    })
    .map(batch => ({
      ...batch,
      type: batch.properties?.upload_type || 'bulk',
      id: batch.off_cycle_payment_group_id,
      batch: `Batch #${batch.batch_number}`,
      employee_name: batch.full_name,
      amount: batch.amount,
    }));

  draftTransfers.value = mappedTransferData
    .filter(transfer => transfer.status === 'DRAFT')
    .sort((a, b) => b.batch_number - a.batch_number);

  processingTransfers.value = mappedTransferData
    .filter(transfer => ['PROCESSING', 'PENDING'].includes(transfer.status))
    .sort((a, b) => b.batch_number - a.batch_number);

  completedTransfers.value = mappedTransferData
    .filter(transfer => ['COMPLETE', 'PROCESSED'].includes(transfer.status))
    .sort((a, b) => b.batch_number - a.batch_number);

  failedTransfers.value = mappedTransferData
    .filter(transfer => transfer.status === 'FAILED')
    .sort((a, b) => b.batch_number - a.batch_number);
};

const formatDateForTable = date => {
  if (!date) {
    return '';
  }
  return format(new Date(date), 'MM/dd/yyyy HH:mm a');
};

const viewBatch = batch => {
  if (batch.properties.upload_type === 'bulk' && batch.status === 'DRAFT') {
    router.push({ name: 'one-time-payments-bulk', query: { batchId: batch.id } });
    return;
  }
  router.push({ name: 'one-time-payment', params: { batchId: batch.id } });
};

const deleteBatch = batch => {
  deleteBatchMutation.mutate(batch.off_cycle_payment_group_id, {
    onSuccess: () => {
      getPaymentsQuery.refetch();
    },
  });
};

const hideBatch = batch => {
  hideBatchMutation.mutate(batch.off_cycle_payment_group_id, {
    onSuccess: () => {
      getPaymentsQuery.refetch();
    },
  });
};

const downloadInvoice = invoiceId => {
  if (downloadInvoiceMutation.isPending.value) {
    return;
  }

  isDownloading.value = invoiceId;

  downloadInvoiceMutation.mutate(invoiceId, {
    onSuccess: async response => {
      const blob = new Blob([response], { type: 'application/pdf' });
      isDownloading.value = null;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `invoice_${invoiceId}.pdf`);
      document.body.append(link);
      link.click();
      link.remove();
    },
    onError: error => {
      isDownloading.value = null;
      console.error('Error downloading invoice:', error);
    },
  });
};
</script>

<style scoped>
.transfer-content {
  padding: 1rem 0;
  width: 100%;
}

.transfer-list {
  background-color: var(--palette-color-base-white);
  border-radius: 0.5rem;
  padding: 2rem;
  width: 100%;
}

.name {
  text-transform: capitalize;
}

.table-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--palette-color-default-lighten-20);
  margin-left: 1rem;
  margin-top: 1rem;
}

.table-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.go-to {
  &:hover {
    cursor: pointer;

    svg {
      fill: var(--palette-color-brand-primary);
    }
  }
}

.no-transfers {
  padding: 1rem;
  text-align: center;
  color: var(--palette-color-default-lighten-20);
}

.processed-via-payroll {
  display: flex;
  justify-content: center;
}

.search-container {
  margin-bottom: 1rem;
}
</style>
